import { useState } from "react";
import {
  useInterviewerMutations,
  useInterviewers,
  useTermInterviewers,
} from "../../hooks";

/**
 * インタビュアー一覧セクションとアサインダイアログの配線をまとめる。
 *
 * 従業員・ステークホルダーどちらのターム詳細でも操作は同じで、確認ダイアログの文言と
 * アサイン時の初期選択（未アサインのデフォルト担当を提案する）を画面ごとに書き分けると
 * 片方だけ直る事故になるため、ここに寄せている。
 *
 * `src/hooks/` ではなくここに置いているのは、返すのが隣の2コンポーネントの props で、
 * データ取得ではなく画面の配線だからである。`src/hooks/` は GraphQL のデータ取得に限る。
 */
export function useTermInterviewerAssignment(termId: string | null) {
  const {
    interviewers: termInterviewers,
    error: termInterviewersError,
    refetch: refetchTermInterviewers,
  } = useTermInterviewers(termId);
  const { interviewers: allInterviewers, error: allInterviewersError } =
    useInterviewers();
  const { removeInterviewerFromTerm, error: interviewerMutationError } =
    useInterviewerMutations();

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignInitialInterviewerIds, setAssignInitialInterviewerIds] =
    useState<string[]>([]);

  const handleAssign = () => {
    const assignedIds = termInterviewers.map(
      (interviewer) => interviewer.interviewerId,
    );
    const defaultIds = allInterviewers
      .filter(
        (interviewer) =>
          interviewer.isDefault &&
          !assignedIds.includes(interviewer.interviewerId),
      )
      .map((interviewer) => interviewer.interviewerId);
    setAssignInitialInterviewerIds(defaultIds);
    setAssignOpen(true);
  };

  const handleRemove = async (interviewerId: string) => {
    if (!termId || !window.confirm("このインタビュアーを削除しますか？"))
      return;
    const result = await removeInterviewerFromTerm(termId, interviewerId);
    if (result?.success) {
      refetchTermInterviewers();
      alert("インタビュアーを削除しました");
      return;
    }
    // 戻り値が無いのは通信・GraphQL の失敗で、その理由は interviewerMutationError に入り
    // 呼び出し側のエラー表示に出る。ここでは操作が完了しなかったことだけを伝える。
    alert(
      result?.errorMessage ||
        "インタビュアーの削除に失敗しました。時間をおいてから再度お試しください",
    );
  };

  return {
    /** インタビュアーの取得に失敗したときの文言。呼び出し側のエラー表示に渡す */
    interviewerDataLoadErrors: [
      termInterviewersError,
      allInterviewersError,
    ].filter((e): e is string => Boolean(e)),
    /** 削除が失敗した理由。定型文に丸められない具体的な理由はこちらに入る */
    interviewerMutationError,
    interviewerListSectionProps: {
      assignedInterviewers: termInterviewers,
      onAssign: handleAssign,
      onRemove: handleRemove,
    },
    interviewerAssignDialogProps: {
      open: assignOpen,
      onOpenChange: setAssignOpen,
      termId,
      termInterviewers,
      allInterviewers,
      initialSelectedInterviewerIds: assignInitialInterviewerIds,
      onAssigned: refetchTermInterviewers,
    },
  };
}
